import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Label } from '@atlaskit/form';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import SmartUserPicker, {
	type Group,
	type OptionData,
	type Value,
} from '@atlaskit/smart-user-picker';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../../../controllers/use-product-dependencies';

import { messages } from './messages';

const updatedStyles = {
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('border.radius.100'),
};

type SegmentationTargetGroupsFieldProps = {
	targetGroupsIds?: string[];
	onGroupSelected: (targetGroupIds: string[] | undefined) => void;
	showUpdatedStyles?: boolean;
	source: string;
};

export const SegmentationTargetGroupsField = ({
	targetGroupsIds,
	onGroupSelected,
	showUpdatedStyles = false,
	source,
}: SegmentationTargetGroupsFieldProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

	const initialGroupIds = useRef<string[]>(targetGroupsIds || []);
	const initialGroups = useRef<Group[]>([]);
	const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

	const { useSegmentationTargetGroups, cloudId, orgId } = useProductDependencies();
	const { data, loading, errorComponent } =
		useSegmentationTargetGroups?.({
			nextCursor,
			initialGroupIds: initialGroupIds.current || [],
		}) || {};

	useEffect(() => {
		const groups: Group[] = [];
		const groupIds = initialGroupIds.current;
		for (const id of groupIds) {
			const group = data?.groups?.nodes?.find((node: any) => node?.id === id);
			if (group && group.id && group.name) {
				groups.push({
					id: group.id,
					name: group.name,
					type: 'group',
				});
			}
		}

		const newGroups = [...initialGroups.current, ...groups];
		initialGroups.current = newGroups;
		if (data?.groups?.pageInfo?.hasNextPage && newGroups.length < groupIds.length) {
			setNextCursor(data.groups.pageInfo.endCursor);
		} else {
			setSelectedGroups(newGroups);
		}
	}, [data]);

	const handleSelectOnChange = useCallback(
		(values: Value) => {
			const groups = values ? (values as Group[]) : [];
			setSelectedGroups(groups);

			onGroupSelected(groups.length > 0 ? groups.map((group) => group.id) : undefined);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'dropdownItem',
					actionSubjectId: 'hubSegmentationTargetGroupsDropdown',
					source,
				},
			}).fire();
		},
		[createAnalyticsEvent, onGroupSelected, source],
	);
	const filterSystemGroups = (options: OptionData[]) => {
		const HIDDEN_GROUP_NAME = 'system-administrators';
		return options.filter((option) => option.name !== HIDDEN_GROUP_NAME);
	};

	return errorComponent ? (
		<>{errorComponent}</>
	) : (
		<Stack>
			<Label htmlFor="card-segmentation-target-groups-selector">
				<Text
					size={showUpdatedStyles ? 'medium' : 'small'}
					weight={showUpdatedStyles ? 'regular' : 'bold'}
					color={showUpdatedStyles ? 'color.text' : 'inherit'}
				>
					<FormattedMessage
						{...(showUpdatedStyles
							? messages.segmentationTargetGroupsLabelV2
							: messages.segmentationTargetGroupsLabel)}
					/>
				</Text>
			</Label>
			<Stack>
				<SmartUserPicker
					filterOptions={filterSystemGroups}
					isLoading={loading}
					fieldId="card-segmentation-target-groups-selector"
					inputId="card-segmentation-target-groups-selector"
					siteId={cloudId}
					orgId={orgId}
					debounceTime={400}
					productKey="confluence"
					includeUsers={false}
					includeGroups
					isMulti
					width="100%"
					value={selectedGroups}
					placeholder=""
					addMoreMessage=""
					onChange={handleSelectOnChange}
					noOptionsMessage={() => null}
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					styles={{
						valueContainer: (base) => ({
							...base,
							minHeight: showUpdatedStyles ? 'auto' : '56px',
							alignItems: 'start',
						}),
						multiValue: (provided) => ({
							...provided,
							...(showUpdatedStyles ? updatedStyles : {}),
							div: {
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							},
						}),
					}}
				/>
			</Stack>
		</Stack>
	);
};
